import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Plus, Minus, Truck, Shield, Lock, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { formatPrice } from '../utils/formatPrice';
import { getProductImage } from '../utils/imageHelper';
import { usePageSEO } from '../hooks/usePageSEO';

export function Cart() {
  usePageSEO({
    title: 'Your Shopping Cart | WellZora Herbal Care',
    description: 'Review your selected herbal products and proceed to checkout.',
    canonical: 'https://wellzoraherbalcare.com/cart',
  });

  const { items, itemCount, subtotal, updateQuantity, removeFromCart, openCart } = useCart();
  const deliveryFee = subtotal >= 3000 || subtotal === 0 ? 0 : 200;
  const total = subtotal + deliveryFee;

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb / Back button */}
        <div className="mb-6">
          <Link 
            to="/shop" 
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mt-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Your Shopping Cart</h1>
            <p className="text-sm text-gray-500">{itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart</p>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm max-w-2xl mx-auto my-8">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5 text-primary">
              <ShoppingBag className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Cart is Currently Empty</h2>
            <p className="text-gray-500 text-sm mb-8 max-w-md mx-auto">
              Looks like you haven't added any herbal remedies yet. Check out our best-sellers and start your wellness journey today.
            </p>
            <Link to="/shop">
              <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Explore Herbal Products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Items Column */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-4">
              {items.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-4 hover:shadow-md transition-shadow"
                >
                  <img 
                    src={getProductImage(item.image)} 
                    alt={item.name} 
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl bg-gray-50 flex-shrink-0"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 text-base mb-0.5">{item.name}</h3>
                    {item.nameUrdu && (
                      <p className="font-urdu text-primary text-xs font-semibold mb-1" dir="rtl">{item.nameUrdu}</p>
                    )}
                    {item.packageItems && item.packageItems.length > 0 ? (
                      <div className="my-2 p-2.5 bg-emerald-50 rounded-xl border border-emerald-100/80">
                        <span className="text-xs font-bold text-emerald-800 block mb-1">🎁 Complete 5-in-1 Course includes:</span>
                        <div className="flex flex-wrap gap-1.5">
                          {item.packageItems.map((p, idx) => (
                            <span key={idx} className="text-xs bg-white text-emerald-900 font-medium px-2 py-0.5 rounded-md border border-emerald-200">
                              ✓ {p.name} ({p.size})
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : item.descriptionUrdu && (
                      <p className="font-urdu text-gray-600 text-xs mb-1.5" dir="rtl">{item.descriptionUrdu}</p>
                    )}
                    {item.size && (
                      <p className="text-xs text-gray-400 mb-2">Size: {item.size}</p>
                    )}
                    <p className="text-primary font-bold text-base">
                      {formatPrice(item.price)}
                    </p>
                  </div>

                  {/* Quantity and Actions */}
                  <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-4 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                    <div className="flex items-center border border-gray-200 rounded-xl bg-gray-50/50">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 text-gray-500 hover:text-primary transition-colors disabled:opacity-40"
                        disabled={item.quantity <= 1}
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-8 text-center text-sm font-semibold text-gray-900">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 text-gray-500 hover:text-primary transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary Column */}
            <div className="lg:col-span-5 xl:col-span-4 bg-white rounded-3xl p-6 border border-gray-100 shadow-md sticky top-28">
              <h2 className="text-xl font-bold text-gray-900 mb-5">Order Summary</h2>

              <div className="space-y-3 text-sm pb-5 border-b border-gray-100">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-900">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Delivery Charges</span>
                  <span className="font-semibold">
                    {deliveryFee === 0 ? (
                      <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded-full text-xs">FREE</span>
                    ) : (
                      formatPrice(deliveryFee)
                    )}
                  </span>
                </div>
                {deliveryFee === 0 ? (
                  <p className="text-xs text-green-600 font-medium">✓ You qualify for Free Delivery across Pakistan!</p>
                ) : (
                  <p className="text-xs text-amber-600">Add {formatPrice(3000 - subtotal)} more for Free Delivery</p>
                )}
              </div>

              <div className="py-4 flex justify-between items-center text-lg font-bold">
                <span className="text-gray-900">Total</span>
                <span className="text-primary text-xl">{formatPrice(total)}</span>
              </div>

              <button
                onClick={openCart}
                className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-primary via-primary-dark to-herbal-800 text-white font-bold text-base shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer mb-3"
              >
                <span>Proceed to WhatsApp Checkout</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <p className="text-xs text-gray-400 text-center mb-6">
                Instant confirmation • Cash on Delivery
              </p>

              {/* Guarantees */}
              <div className="space-y-3 pt-4 border-t border-gray-100 text-xs text-gray-600">
                <div className="flex items-center gap-2.5">
                  <Truck className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>3–5 Business Days delivery across Pakistan</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>100% Herbal & DRAP Certified formulas</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Lock className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Discreet and securely packed parcel</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}